// ProfileContext.js - Fixed with correct SelfLearner endpoint
import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

export const ProfileContext = createContext();

export const ProfileProvider = ({ children }) => {
  // Initialize state from localStorage or null if not checked
  const [profileState, setProfileState] = useState(() => {
    const saved = localStorage.getItem('profileState');
    return saved ? JSON.parse(saved) : { complete: null, loading: true };
  });

  const role = (localStorage.getItem("role") || "").toLowerCase().trim();
  const BASE_URL = process.env.REACT_APP_API_URL;


  const getProfileStatusUrl = useCallback(() => {
    // Skip profile check for Admin users
    if (role === "admin") {
      return null;
    }
    
    switch (role) {
      case "manager":
        return `${BASE_URL}/Manager/ProfileUpdateStatus`;
      case "mentor":
        return `${BASE_URL}/Mentor/ProfileUpdateStatus`;
      case "learner":
        return `${BASE_URL}/Learners/ProfileUpdateStatus`;
      case "selflearner":
        return `${BASE_URL}/indv/SelfLearner/ProfileUpdateStatus`; 
    
      case "expert":
        return `${BASE_URL}/indv/Experts/ProfileUpdateStatus`; 
               
      default: 
        return null;
    }
  }, [role, BASE_URL]);

  const fetchProfileStatus = useCallback(async () => {
    const url = getProfileStatusUrl();

    // If user is Admin, automatically mark profile as complete
    if (role === "admin") {
      updateProfileState(true, false);
      return;
    }

    if (!url) {
      updateProfileState(false, false);
      return;
    }

    try {
      updateProfileState(null, true);
      const token = localStorage.getItem("token");
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const completed = response.data?.isProfileCompleted;
      console.log(`Profile status for ${role}:`, { 
        isProfileCompleted: completed,
        data: response.data 
      });
      updateProfileState(completed, false);
    } catch (error) {
      console.error(`Failed to fetch profile status for ${role}:`, error);
      // Don't show toast for SelfLearner if endpoint doesn't exist yet
      if (role !== "selflearner") {
        toast.error("Failed to fetch profile status");
      }
      updateProfileState(false, false);
    }
  }, [getProfileStatusUrl, role]);

  const updateProfileState = (complete, loading) => {
    const newState = { complete, loading };
    setProfileState(newState);
    localStorage.setItem('profileState', JSON.stringify(newState));
    console.log(`Updated profile state for ${role}:`, newState);
  };

  useEffect(() => {
    fetchProfileStatus();
  }, [fetchProfileStatus]);

  const markProfileComplete = () => {
    console.log(`Manually marking profile complete for ${role}`);
    updateProfileState(true, false);
  };

  const value = {
    isProfileComplete: role === "admin" ? true : profileState.complete,
    loading: profileState.loading,
    fetchProfileStatus,
    markProfileComplete,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

ProfileProvider.propTypes = {
  children: PropTypes.node.isRequired,
};